// BroadcastNewsRow — one full-bleed, hue-rotated announcement row inside the
// home BroadcastWidget (2026-06-12 per Nika "BroadcastWidget does not show
// news"). Recolored 2026-08-09 per the geex reference frame 11
// (workspace/design-refs/geex/11-cards-mail-colored-rows.png,
// tickets/brief-product-ui-direction.md "colored mail rows"): muted-hue row
// rotation, avatar circle, dashed-ring star, name+time on one baseline,
// subject+date at the ink.55 secondary floor. NAMED component on purpose:
// inline `childExtends: { ... }` does not bind onClick in DOMQL v3.14 (same
// gotcha the meet TranscriptPage documents) — rows must extend a registered
// component. Opens the same windows detail layer IntranetRows uses
// (selectedAnnouncement carrier + 'announcement-detail').
//
// Row is a DIV with role="button"/tabindex (not a native <button>) because it
// hosts a genuinely interactive nested control (the favorite Star, a real
// <button>) — nesting <button> inside <button> is invalid HTML5 (the parser
// auto-closes the outer tag, silently pulling every sibling after the inner
// button OUT of the row). Same recipe as SidebarRow.js (role/tabindex +
// onKeydown mirroring Enter/Space).
//
// Hue = the BroadcastWidget's OWN card colour, resolved through
// `homeWidgetSurfaceHue(root, 'broadcast')`. The row is an ink-alpha plate on
// that colour (`.fill` = ink.12), its text the colour's ink / inkSoft — the
// "one hue per widget, ink-alpha secondary system" contract in
// functions/homeWidgetHue.js.
// It used to rotate a SECOND hue family per row (`rowHueRotation(rowIndex,
// 'muted')`: brown/green/orange/red, white ink — the geex colored-mail rows,
// which sit on a NEUTRAL card in the reference) INSIDE the pink poster, so
// the first row painted opaque brown-on-pink and read as mud (Nika
// 2026-08-15 "fix colors on widgets"; design-direction.md §6 multicolor
// lives ACROSS the set, never within one card — the tickets pills already
// dropped their second hue for the same reason). `s.rowIndex` still arrives
// from pages/main.js BroadcastWidget.News for AdminChrome-style consumers;
// this row no longer reads it. Called inline everywhere (no module-scope
// helper) — a closure over a module-local const does not survive frank JSON
// serialization.
//
// ⚠️ EVERY read below is NULL-GUARDED with an ADAPTIVE neutral fallback
// (2026-09-02, "make solid - a default color for all of them"). The widget's
// colour is now OPT-IN, so `homeWidgetSurfaceHue` returns null on a board
// where nobody picked one — which used to be impossible for 'broadcast' and
// is now the default. An unguarded `.ink` would throw and kill the row's
// whole paint effect; a poster ink kept as the fallback would paint black
// text on the neutral card in dark mode (the HOME-POSTER-NEUTRAL-INK-ON-HUE-1
// failure, mirrored). The fallbacks are the shell's own adaptive tokens.
export const BroadcastNewsRow = {
  flow: 'y',
  gap: 'Z1',
  padding: 'A A1',
  round: 'radiusCard',
  textAlign: 'left',
  cursor: 'pointer',
  position: 'relative',
  transition: '160ms defaultBezier transform',
  color: (el, s) => (el.call('homeWidgetSurfaceHue', s.root, 'broadcast') || {}).ink || 'title',
  background: (el, s) =>
    (el.call('homeWidgetSurfaceHue', s.root, 'broadcast') || {}).fill || 'overlay',
  ':hover': { transform: 'translateY(-2px)' },
  // No `:focus-visible` outline here, deliberately. This row is keyboard-
  // reachable (`role: 'button'` + `tabindex: '0'`) and gets its ring from the
  // GLOBAL brand one — DESIGN_LANGUAGE §8.2: components never re-implement
  // focus. The two rules removed here were `outline: 'W solid blue'` +
  // `outlineOffset: 'W'`, which rendered NOTHING: the outline family is in
  // css-in-props' DEFAULT_CSS_PROPERTIES_LIST, whose branch emits values
  // verbatim with no token lookup, so the browser received the literal
  // `outline: solid W blue` and dropped it. Removing them changes zero
  // pixels and lets the global ring through unambiguously. Same defect
  // already removed from infra/ServiceNode.js (workspace 4ccaa333).
  role: 'button',
  tabindex: '0',

  Top: {
    flow: 'x',
    align: 'flex-start space-between',

    Avatar: {
      boxSize: 'C',
      round: '50%',
      src: (el, s) => el.call('avatarUrlFor', s.author)
    },

    // Dashed-ring star — design-direction.md §8's ONLY sanctioned stroke:
    // dashed ink.35 = inactive/selectable, solid ink fill = active. Local
    // toggle only (workspaceProject.announcements has no starred field to
    // persist to) — still genuinely wired (not a fake affordance): it
    // flips s.starred and repaints immediately.
    Star: {
      tag: 'button',
      type: 'button',
      boxSize: 'B1',
      round: '50%',
      align: 'center center',
      padding: '0',
      cursor: 'pointer',
      background: (el, s) =>
        s.starred
          ? (el.call('homeWidgetSurfaceHue', s.root, 'broadcast') || {}).ink || 'title'
          : 'transparent',
      borderWidth: 'hairline',
      borderStyle: (el, s) => (s.starred ? 'solid' : 'dashed'),
      borderColor: (el, s) => {
        const hue = el.call('homeWidgetSurfaceHue', s.root, 'broadcast')
        if (!hue) return s.starred ? 'title' : 'line'
        return s.starred ? hue.ink : hue.ink + '.35'
      },
      transition: '120ms defaultBezier background, 120ms defaultBezier border-color',
      'aria-label': (el) => el.call('polyglot', 'workspace.newsRow.toggleFavorite'),
      'aria-pressed': (el, s) => !!s.starred,

      Icon: {
        icon: (el, s) => (s.starred ? 'star' : 'starOutline'),
        boxSize: 'Y1',
        color: (el, s) => {
          const hue = el.call('homeWidgetSurfaceHue', s.root, 'broadcast')
          // A STARRED glyph sits on the filled ink disc, so it must be the
          // ink's counterpart: the colour's own bg on a picked colour, and
          // `title-reversed` (the shell's inverted-ink token) on the neutral
          // card, where the disc is `title`.
          if (!hue) return s.starred ? 'title-reversed' : 'title'
          return s.starred ? hue.bg : hue.ink
        }
      },

      onClick: (e, el, s) => {
        e.stopPropagation()
        s.update({ starred: !s.starred })
      }
    }
  },

  NameLine: {
    flow: 'x',
    align: 'baseline space-between',
    gap: 'Z',

    Name: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '700',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      text: (el, s) => s.author || ''
    },
    Time: {
      tag: 'span',
      fontFamily: 'Code',
      fontSize: 'A',
      fontWeight: '700',
      flexShrink: '0',
      text: (el, s) => el.call('formatTime', s.date)
    }
  },

  SubjectLine: {
    flow: 'x',
    align: 'baseline space-between',
    gap: 'Z',

    Subject: {
      tag: 'span',
      fontSize: 'Z1',
      fontWeight: '400',
      color: (el, s) =>
        (el.call('homeWidgetSurfaceHue', s.root, 'broadcast') || {}).inkSoft || 'caption',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      flex: '1',
      minWidth: '0',
      text: (el, s) => s.title || ''
    },
    Date: {
      tag: 'span',
      fontFamily: 'Code',
      fontSize: 'Z1',
      fontWeight: '400',
      color: (el, s) =>
        (el.call('homeWidgetSurfaceHue', s.root, 'broadcast') || {}).inkSoft || 'caption',
      flexShrink: '0',
      text: (el, s) => el.call('formatShortDate', s.date)
    }
  },

  onKeydown: (e, el, s, ctx) => {
    if ((e.key === 'Enter' || e.key === ' ') && el.onClick) {
      const t = e.target
      if (t && t.closest && t.closest('button')) return
      e.preventDefault()
      el.onClick(e, el, s, ctx)
    }
  },

  onClick: (e, el, s) => {
    const t = e.target
    if (t && t.closest && t.closest('button')) return
    s.root.update({ selectedAnnouncement: s })
    el.call('openModal', {
      id: 'announcement-detail',
      template: 'custom',
      width: 'overlayXl',
      maxHeight: '85vh',
      content: { extends: 'AnnouncementDetailModal' }
    })
  }
}
